import { motion } from 'framer-motion';
import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

// Email validation regex pattern - can be adjusted as needed
const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Replace with your actual Cloudflare site key
const TURNSTILE_SITE_KEY = '0x4AAAAAABgceIXtdb0T2uGy';

// Replace with your actual API endpoint
const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
if (!API_ENDPOINT) {
  throw new Error('REACT_APP_API_ENDPOINT is not defined');
}

interface FormData {
  name: string;
  email: string;
  company: string;
  message: string;
  privacy: boolean;
}

interface FormErrors {
  name: string;
  email: string;
  company: string;
  message: string;
  turnstile: string;
  submit: string;
  privacy: string;
}

declare global {
  interface Window {
    turnstile: {
      render: (container: string | HTMLElement, options: any) => string;
      reset: (widgetId: string) => void;
    };
  }
}

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    message: '',
    privacy: false,
  });

  const [errors, setErrors] = useState<FormErrors>({
    name: '',
    email: '',
    company: '',
    message: '',
    turnstile: '',
    submit: '',
    privacy: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const scriptLoaded = useRef(false);
  const location = useLocation();

  useEffect(() => {
    // Pre-fill message from URL query parameter
    const params = new URLSearchParams(location.search);
    const message = params.get('message');
    if (message) {
      setFormData(prev => ({ ...prev, message }));
    }
  }, [location.search]);

  useEffect(() => {
    // Only load the script if it hasn't been loaded yet
    if (!scriptLoaded.current) {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
      scriptLoaded.current = true;

      script.onload = () => {
        if (window.turnstile && !turnstileWidgetId.current) {
          turnstileWidgetId.current = window.turnstile.render('#turnstile-container', {
            sitekey: TURNSTILE_SITE_KEY,
            theme: 'dark',
            callback: (token: string) => {
              setTurnstileToken(token);
              setErrors((prev) => ({
                ...prev,
                turnstile: '',
              }));
            },
            'error-callback': () => {
              setTurnstileToken(null);
              setErrors((prev) => ({
                ...prev,
                turnstile: 'Security check failed. Please try again.',
              }));
            },
          });
        }
      };
    }

    return () => {
      // Only cleanup if we have a widget ID
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, []);

  const validateForm = () => {
    const newErrors: FormErrors = {
      name: '',
      email: '',
      company: '',
      message: '',
      turnstile: '',
      submit: '',
      privacy: '',
    };

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    // Turnstile validation
    if (!turnstileToken) {
      newErrors.turnstile = 'Please complete the security check';
    }

    // Privacy policy checkbox validation
    if (!formData.privacy) {
      newErrors.privacy = 'You must agree to the privacy policy.';
    }

    setErrors(newErrors);
    return !Object.values(newErrors).some(error => error !== '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setErrors(prev => ({ ...prev, submit: '' }));

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: 'contact',
          name: formData.name,
          email: formData.email,
          company: formData.company,
          message: formData.message,
          privacyPolicyAccepted: formData.privacy,
          'cf-turnstile-response': turnstileToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        // Use the error message from the backend, or a default
        throw new Error(errorData.error || 'Failed to submit form');
      }

      // Reset form after successful submission
      setFormData({
        name: '',
        email: '',
        company: '',
        message: '',
        privacy: false,
      });
      setErrors({
        name: '',
        email: '',
        company: '',
        message: '',
        turnstile: '',
        submit: '',
        privacy: '',
      });
      setTurnstileToken(null);
      setSubmitSuccess(true);
      
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
      setErrors(prev => ({
        ...prev,
        submit: errorMessage,
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));

    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Clear success message when form is modified
    if (submitSuccess) {
      setSubmitSuccess(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact | nautdigital</title>
        <meta name="description" content="Get in touch with nautdigital for bespoke digital solutions, project inquiries, or consultations." />
      </Helmet>
      <div className="relative min-h-screen overflow-hidden">
        {/* Top gradient overlay, full width */}
        <div className="absolute top-0 left-0 w-full h-64 bg-gradient-to-b from-dark-lighter to-black pointer-events-none z-0" />
        <div className="container mx-auto pt-32 pb-16 px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Get in Touch
            </h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Let's discuss how we can help transform your business with our services.
            </p>
          </motion.div>

          <div id="contact-form" className="grid grid-cols-1 md:grid-cols-2 gap-12 scroll-mt-28">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6">
                {submitSuccess && (
                  <div className="bg-green-500/10 border border-green-500 text-green-500 px-4 py-3 rounded-lg">
                    Thank you for your message! We'll get back to you soon.
                  </div>
                )}

                {errors.submit && (
                  <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg">
                    {errors.submit}
                  </div>
                )}

                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-dark-lighter border ${
                      errors.name ? 'border-red-500' : 'border-gray-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                    required
                  />
                  {errors.name && (
                    <p className="mt-1 text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="email"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 bg-dark-lighter border ${
                      errors.email ? 'border-red-500' : 'border-gray-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                    required
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                <div>
                  <label
                    htmlFor="company"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Company
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-dark-lighter border border-gray-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-300 mb-2"
                  >
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={6}
                    className={`w-full px-4 py-2 bg-dark-lighter border ${
                      errors.message ? 'border-red-500' : 'border-gray-700'
                    } rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary`}
                    required
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">{errors.message}</p>
                  )}
                </div>

                <div>
                  <div id="turnstile-container" className="flex justify-center" />
                  {errors.turnstile && (
                    <p className="mt-1 text-sm text-red-500">{errors.turnstile}</p>
                  )}
                </div>

                <div>
                  <label className="flex items-center space-x-2 text-gray-300">
                    <input
                      type="checkbox"
                      name="privacy"
                      checked={formData.privacy}
                      onChange={handleChange}
                      className="form-checkbox h-5 w-5 text-accent rounded focus:ring-accent border-gray-700 bg-dark-lighter"
                      required
                    />
                    <span>
                      I've read and accept the
                      <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-accent underline ml-1">privacy policy</a>.
                    </span>
                  </label>
                  {errors.privacy && (
                    <p className="mt-1 text-sm text-red-500">{errors.privacy}</p>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full px-4 py-3 rounded-lg font-medium text-lg text-white bg-primary hover:bg-primary-dark transition-all duration-200 ${
                    isSubmitting ? 'opacity-75 cursor-not-allowed' : ''
                  }`}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="space-y-8"
            >

              <div className="bg-white/5 backdrop-blur-lg border-2 border-accent/20 shadow-2xl rounded-lg p-6">
                <h2 className="text-2xl font-bold text-white mb-4">
                  Why choose us?
                </h2>
                <ul className="space-y-3 text-gray-300">
                  <li className="flex items-center space-x-2">
                    <span className="text-primary">✓</span>
                    <span>Years of experience, helping some big names achieve digital success.</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-primary">✓</span>
                    <span>We'll work collaboratively with you every step of the way.</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-primary">✓</span>
                    <span>Our pricing is fixed, you pay only for your requirements.</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-primary">✓</span>
                    <span>You'll own your solution's source code outright from day 1.</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <span className="text-primary">✓</span>
                    <span>We offer dedicated support and management, so you can stay in safe hands if you wish to.</span>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact; 